import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const WHATSAPP_NUM = '971547172715';
const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d727g3c991ove783tceg';
const PARTNERSHIP_FORM_URL = 'https://readdy.ai/api/form/d7622jdr5g02gnd69ptg';

type Tab = 'contact' | 'partnership';

/* ─── General Contact Form ─── */
function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { setError('يرجى ملء جميع الحقول المطلوبة'); return; }
    if (form.message.length > 500) { setError('يجب أن تكون الرسالة أقل من 500 حرف'); return; }
    setError(''); setSubmitting(true);
    try {
      await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        body: new URLSearchParams({ name: form.name, email: form.email, phone: form.phone, subject: form.subject, message: form.message }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setSubmitted(true);
    } catch { setSubmitted(true); } finally { setSubmitting(false); }
  };

  if (submitted) return <SuccessCard message="تم إرسال رسالتك! سنتواصل معك في أقرب وقت ممكن." />;

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InputField label="الاسم الكامل" name="name" value={form.name} onChange={handleChange} placeholder="اسمك الكامل" required />
        <InputField label="البريد الإلكتروني" name="email" type="email" value={form.email} onChange={handleChange} placeholder="example@email.com" required />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InputField label="رقم الهاتف" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+971XXXXXXXXX" />
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">الموضوع</label>
          <select name="subject" value={form.subject} onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
            <option value="">اختر الموضوع</option>
            <option value="استفسار منتج">استفسار عن منتج</option>
            <option value="طلب سابق">متابعة طلب سابق</option>
            <option value="شكوى">شكوى</option>
            <option value="اقتراح">اقتراح</option>
            <option value="أخرى">أخرى</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">الرسالة <span className="text-red-400">*</span></label>
        <textarea name="message" value={form.message} onChange={handleChange} placeholder="اكتب رسالتك هنا..." rows={5} maxLength={500}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-right resize-none" required />
        <p className={`text-xs mt-1 text-left ${form.message.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>{form.message.length}/500</p>
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <SubmitButton submitting={submitting} label="إرسال الرسالة" />
    </form>
  );
}

/* ─── Partnership Form ─── */
function PartnershipForm() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    businessType: '', country: '', productsInterest: '',
    monthlyQuantity: '', orderQuantity: '', experience: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.businessType || !form.productsInterest || !form.monthlyQuantity) {
      setError('يرجى ملء جميع الحقول المطلوبة المُشار إليها بـ *'); return;
    }
    setError(''); setSubmitting(true);
    try {
      await fetch(PARTNERSHIP_FORM_URL, {
        method: 'POST',
        body: new URLSearchParams({
          name: form.name, email: form.email, phone: form.phone,
          company: form.company, businessType: form.businessType,
          country: form.country, productsInterest: form.productsInterest,
          monthlyQuantity: form.monthlyQuantity, orderQuantity: form.orderQuantity,
          experience: form.experience, message: form.message,
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setSubmitted(true);
    } catch { setSubmitted(true); } finally { setSubmitting(false); }
  };

  if (submitted) return (
    <SuccessCard message="تم استلام طلب شراكتك بنجاح! سيتواصل معك فريقنا خلال 24 ساعة." />
  );

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">

      {/* Section: بيانات صاحب العمل */}
      <FormSection title="بيانات صاحب العمل" icon="ri-user-line">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <InputField label="الاسم الكامل" name="name" value={form.name} onChange={handleChange} placeholder="اسمك الكامل" required />
          <InputField label="البريد الإلكتروني" name="email" type="email" value={form.email} onChange={handleChange} placeholder="example@email.com" required />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <InputField label="رقم الهاتف / واتساب" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+971XXXXXXXXX" required />
          <InputField label="اسم الشركة أو المتجر" name="company" value={form.company} onChange={handleChange} placeholder="اختياري — إن وُجد" />
        </div>
      </FormSection>

      {/* Section: نوع العمل */}
      <FormSection title="نوع النشاط التجاري" icon="ri-store-2-line">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              نوع العمل <span className="text-red-400">*</span>
            </label>
            <select name="businessType" value={form.businessType} onChange={handleChange} required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
              <option value="">اختر نوع العمل</option>
              <option value="متجر إلكتروني">متجر إلكتروني (أونلاين)</option>
              <option value="متجر فيزيائي">متجر أو محل فيزيائي</option>
              <option value="موزع">موزع إقليمي</option>
              <option value="بائع تجزئة">بائع تجزئة</option>
              <option value="صالون تجميل">صالون تجميل أو سبا</option>
              <option value="مستورد">مستورد ومورّد</option>
              <option value="بيع من المنزل">بيع من المنزل / سوشيال ميديا</option>
              <option value="أخرى">أخرى</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">الدولة / المنطقة</label>
            <select name="country" value={form.country} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
              <option value="">اختر الدولة</option>
              <option value="الإمارات">الإمارات العربية المتحدة</option>
              <option value="السعودية">المملكة العربية السعودية</option>
              <option value="الكويت">الكويت</option>
              <option value="قطر">قطر</option>
              <option value="البحرين">البحرين</option>
              <option value="عُمان">سلطنة عُمان</option>
              <option value="الأردن">الأردن</option>
              <option value="مصر">مصر</option>
              <option value="أخرى">دولة أخرى</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            المنتجات المطلوبة <span className="text-red-400">*</span>
          </label>
          <select name="productsInterest" value={form.productsInterest} onChange={handleChange} required
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
            <option value="">اختر نوع المنتجات</option>
            <option value="عطور فقط">عطور فقط</option>
            <option value="مستحضرات تجميل فقط">مستحضرات التجميل فقط</option>
            <option value="عطور ومستحضرات">عطور ومستحضرات التجميل معاً</option>
            <option value="عود وبخور">عود وبخور</option>
            <option value="عناية بالشعر">منتجات العناية بالشعر</option>
            <option value="عناية بالبشرة">منتجات العناية بالبشرة</option>
            <option value="الكل">جميع الفئات</option>
          </select>
        </div>
      </FormSection>

      {/* Section: الكميات المطلوبة */}
      <FormSection title="الكميات والاحتياجات" icon="ri-shopping-basket-2-line">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              الكمية الشهرية المتوقعة <span className="text-red-400">*</span>
            </label>
            <select name="monthlyQuantity" value={form.monthlyQuantity} onChange={handleChange} required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
              <option value="">اختر نطاق الكمية</option>
              <option value="أقل من 50 قطعة">أقل من 50 قطعة / شهر</option>
              <option value="50-200 قطعة">50 – 200 قطعة / شهر</option>
              <option value="200-500 قطعة">200 – 500 قطعة / شهر</option>
              <option value="500-1000 قطعة">500 – 1,000 قطعة / شهر</option>
              <option value="أكثر من 1000 قطعة">أكثر من 1,000 قطعة / شهر</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">حجم الطلب الأول المتوقع</label>
            <select name="orderQuantity" value={form.orderQuantity} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
              <option value="">اختر حجم الطلب</option>
              <option value="أقل من 1000 درهم">أقل من 1,000 درهم</option>
              <option value="1000-5000 درهم">1,000 – 5,000 درهم</option>
              <option value="5000-15000 درهم">5,000 – 15,000 درهم</option>
              <option value="15000-50000 درهم">15,000 – 50,000 درهم</option>
              <option value="أكثر من 50000 درهم">أكثر من 50,000 درهم</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">خبرتك في مجال العطور والتجميل</label>
          <select name="experience" value={form.experience} onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 cursor-pointer text-right bg-white">
            <option value="">اختر مستوى الخبرة</option>
            <option value="مبتدئ">أبدأ لأول مرة في هذا المجال</option>
            <option value="أقل من سنة">أقل من سنة</option>
            <option value="1-3 سنوات">1 – 3 سنوات</option>
            <option value="3-7 سنوات">3 – 7 سنوات</option>
            <option value="أكثر من 7 سنوات">أكثر من 7 سنوات</option>
          </select>
        </div>
      </FormSection>

      {/* Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">ملاحظات أو طلبات خاصة</label>
        <textarea name="message" value={form.message} onChange={handleChange}
          placeholder="أي تفاصيل إضافية تريد مشاركتها معنا..." rows={4} maxLength={500}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-right resize-none" />
        <p className={`text-xs mt-1 text-left ${form.message.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>{form.message.length}/500</p>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Benefits reminder */}
      <div className="bg-[#C9A961]/8 rounded-2xl p-5 grid grid-cols-2 gap-3">
        {[
          { icon: 'ri-vip-crown-line', text: 'أسعار جملة حصرية' },
          { icon: 'ri-truck-line', text: 'توصيل لجميع دول الخليج' },
          { icon: 'ri-customer-service-2-line', text: 'مدير حساب مخصص' },
          { icon: 'ri-gift-2-line', text: 'عروض خاصة للشركاء' },
        ].map(b => (
          <div key={b.text} className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-[#C9A961]/15 rounded-lg flex-shrink-0">
              <i className={`${b.icon} text-[#C9A961] text-sm`} />
            </div>
            <span className="text-gray-700 text-xs font-medium">{b.text}</span>
          </div>
        ))}
      </div>

      <SubmitButton submitting={submitting} label="إرسال طلب الشراكة" />
    </form>
  );
}

/* ─── Shared Sub-components ─── */
function FormSection({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
        <div className="w-7 h-7 flex items-center justify-center bg-[#C9A961]/10 rounded-lg">
          <i className={`${icon} text-[#C9A961] text-sm`} />
        </div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InputField({ label, name, value, onChange, placeholder, type = 'text', required = false }: {
  label: string; name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required}
        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-right" />
    </div>
  );
}

function SubmitButton({ submitting, label }: { submitting: boolean; label: string }) {
  return (
    <button type="submit" disabled={submitting}
      className="w-full bg-[#C9A961] text-white py-4 rounded-xl font-bold text-base hover:bg-[#A07C30] transition-colors cursor-pointer disabled:opacity-70 whitespace-nowrap flex items-center justify-center gap-2">
      {submitting ? (
        <><i className="ri-loader-4-line animate-spin text-lg" /><span>جاري الإرسال...</span></>
      ) : (
        <><span>{label}</span><i className="ri-send-plane-line text-lg" /></>
      )}
    </button>
  );
}

function SuccessCard({ message }: { message: string }) {
  return (
    <div className="text-center py-16 px-8">
      <div className="w-20 h-20 flex items-center justify-center bg-[#C9A961]/10 rounded-full mx-auto mb-5">
        <i className="ri-checkbox-circle-line text-4xl text-[#C9A961]" />
      </div>
      <h3 className="text-2xl font-black text-gray-900 mb-3">تم بنجاح!</h3>
      <p className="text-gray-500 text-base leading-relaxed">{message}</p>
      <a
        href={`https://wa.me/${WHATSAPP_NUM}?text=مرحباً، أرسلت طلبي عبر الموقع وأريد التأكيد`}
        target="_blank"
        rel="nofollow noreferrer"
        className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-white px-8 py-3 rounded-full font-bold text-sm hover:bg-[#1da851] transition-colors cursor-pointer whitespace-nowrap"
      >
        <div className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-lg" /></div>
        تأكيد عبر واتساب
      </a>
    </div>
  );
}

/* ─── Main Page ─── */
export default function ContactPage() {
  const [activeTab, setActiveTab] = useState<Tab>('partnership');

  return (
    <main dir="rtl">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-0 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9cf5cb8e5ec3ba8f6cbb08988a2dfc0b.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-white/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pb-0">
          <div className="text-right pb-14">
            <span className="inline-flex items-center gap-2 text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-4">
              <div className="w-8 h-px bg-[#C9A961]" />
              تواصل مع نجاحات
            </span>
            <h1 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4 leading-tight">
              ابدأ رحلتك معنا
              <br />
              <span className="text-[#C9A961]">شراكة تجارية حقيقية</span>
            </h1>
            <p className="text-gray-500 text-lg max-w-xl">
              سواء كانت لديك استفسارات أو تريد الانضمام إلى شبكة تجار نجاحات،
              فريقنا مستعد دائماً للدعم والمساعدة
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-[#F5F1E8] p-1 rounded-full w-fit">
            {([
              { id: 'partnership' as Tab, label: 'طلب شراكة رسمي', icon: 'ri-links-line' },
              { id: 'contact' as Tab, label: 'راسلنا', icon: 'ri-mail-send-line' },
            ] as { id: Tab; label: string; icon: string }[]).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id ? 'bg-[#C9A961] text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-base`} />
                </div>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-14 px-6 lg:px-12 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Sidebar: Contact Info */}
          <aside className="space-y-5">
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-lg font-black text-gray-900 mb-5">معلومات التواصل</h2>
              <div className="space-y-4">
                {[
                  { icon: 'ri-whatsapp-line', label: 'واتساب', value: `+${WHATSAPP_NUM}`, href: `https://wa.me/${WHATSAPP_NUM}`, color: 'text-[#25D366]' },
                  { icon: 'ri-phone-line', label: 'هاتف', value: `+${WHATSAPP_NUM}`, href: `tel:+${WHATSAPP_NUM}`, color: 'text-[#C9A961]' },
                  { icon: 'ri-mail-line', label: 'بريد إلكتروني', value: 'aliix009x@gmail.com', href: 'mailto:aliix009x@gmail.com', color: 'text-[#C9A961]' },
                  { icon: 'ri-map-pin-line', label: 'الموقع', value: 'دبي، الإمارات العربية المتحدة', href: 'https://maps.google.com/?q=Dubai', color: 'text-[#C9A961]' },
                  { icon: 'ri-time-line', label: 'ساعات العمل', value: 'السبت – الخميس: 9ص – 11م', href: null, color: 'text-[#C9A961]' },
                ].map(c => (
                  <div key={c.label} className="flex items-start gap-3 p-3 bg-[#FAFAF8] rounded-xl">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                      <i className={`${c.icon} text-lg ${c.color}`} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} target="_blank" rel="nofollow noreferrer"
                          className="text-gray-900 font-semibold text-sm hover:text-[#C9A961] transition-colors cursor-pointer"
                          dir={['واتساب', 'هاتف', 'بريد إلكتروني'].includes(c.label) ? 'ltr' : 'auto'}>
                          {c.value}
                        </a>
                      ) : (
                        <p className="text-gray-900 font-semibold text-sm">{c.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/${WHATSAPP_NUM}?text=مرحباً، أريد الاستفسار عن الشراكة التجارية مع نجاحات`}
              target="_blank" rel="nofollow noreferrer"
              className="flex items-center justify-center gap-3 bg-[#25D366] text-white w-full py-4 rounded-2xl font-bold text-sm hover:bg-[#1da851] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-xl" /></div>
              تواصل مباشر عبر واتساب
            </a>

            {/* Partnership perks */}
            {activeTab === 'partnership' && (
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-sm font-black text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center"><i className="ri-star-line text-[#C9A961]" /></div>
                  مزايا الشراكة معنا
                </h3>
                <ul className="space-y-3">
                  {[
                    'أسعار جملة حصرية وتنافسية',
                    'مدير حساب مخصص لك شخصياً',
                    'أولوية في الطلبات والمخزون',
                    'عروض وخصومات حصرية للشركاء',
                    'دعم تسويقي ومواد ترويجية',
                    'توصيل مجاني للطلبات الكبيرة',
                  ].map(p => (
                    <li key={p} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-[#C9A961]/10 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-[#C9A961] text-xs" />
                      </div>
                      <span className="text-gray-600 text-xs">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Map */}
            <div className="w-full h-44 rounded-2xl overflow-hidden">
              <iframe
                title="موقع المتجر"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1785!2d55.2708!3d25.2048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43348a67e24b%3A0xff45e502e1cbb7e5!2sDubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sar!2sae!4v1"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          {/* Main Form Area */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 lg:p-10">
              {activeTab === 'partnership' ? (
                <>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C9A961]/10 rounded-xl">
                      <i className="ri-links-line text-xl text-[#C9A961]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-gray-900">طلب شراكة تجارية رسمية</h2>
                      <p className="text-gray-400 text-xs mt-0.5">سيتم مراجعة طلبك والتواصل معك خلال 24 ساعة</p>
                    </div>
                  </div>
                  <PartnershipForm />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C9A961]/10 rounded-xl">
                      <i className="ri-mail-send-line text-xl text-[#C9A961]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-gray-900">أرسل لنا رسالة</h2>
                      <p className="text-gray-400 text-xs mt-0.5">سنرد عليك في أقرب وقت ممكن</p>
                    </div>
                  </div>
                  <ContactForm />
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
